// src/pages/Login.tsx
import './Login.css';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const BG_IMAGES = [
  '/assets/img/login-bg/BBVA.jpg',
  '/assets/img/login-bg/BCP.jpg',
  '/assets/img/login-bg/FarmaciasPeruanas.jpg',
  '/assets/img/login-bg/Interbank.jpg',
  '/assets/img/login-bg/Scotiabank.jpg',
];

// Extrae códigos de rol de múltiples formatos posibles
function roleCodesFrom(input: unknown): string[] {
  if (!input) return [];
  if (Array.isArray(input)) {
    return input
      .map((r: any) => (typeof r === 'string' ? r : r?.code || r?.role || r?.name))
      .filter(Boolean)
      .map((s) => String(s).toUpperCase());
  }
  if (typeof input === 'object' && input) {
    // p.ej. { items:[{code:'X'}] } o { roles:[...] }
    const arr = (input as any).items || (input as any).data || (input as any).roles || [];
    return roleCodesFrom(arr);
  }
  return [];
}

export default function Login() {
  const { login, user: ctxUser } = useAuth();
  const navigate = useNavigate();

  const [bgIndex, setBgIndex] = useState(0);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(() => localStorage.getItem('remember_me') === '1');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  // Pre-carga de imágenes (evita parpadeos)
  useEffect(() => {
    BG_IMAGES.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  // Rotación de fondo cada 10s
  useEffect(() => {
    const id = setInterval(() => {
      setBgIndex((i) => (i + 1) % BG_IMAGES.length);
    }, 10000);
    return () => clearInterval(id);
  }, []);

  // Si ya está logueado, redirige (evita ver el login otra vez)
  const initialUser = useMemo(() => {
    if (ctxUser) return ctxUser;
    const raw = localStorage.getItem('user') ?? sessionStorage.getItem('user');
    try {
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, [ctxUser]);

  useEffect(() => {
    if (!initialUser) return;
    const codes = roleCodesFrom(initialUser.roles);
    // Si tiene algún rol DEVIOZ_, lo mandamos a su dashboard
    if (codes.some((c) => c.startsWith('DEVIOZ_'))) {
      navigate('/devioz/dashboard', { replace: true });
    } else {
      navigate('/dashboard', { replace: true });
    }
  }, [initialUser, navigate]);

  // Login
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;

    setError('');
    setLoading(true);

    try {
      await login(email.trim(), password);

      if (remember) localStorage.setItem('remember_me', '1');
      else localStorage.removeItem('remember_me');

      // No navegamos aquí: AuthContext ya redirige tras login a /devioz/dashboard
    } catch (err: any) {
      const status = err?.response?.status;
      const msg =
        status === 401
          ? 'Credenciales inválidas. Intenta nuevamente.'
          : err?.response?.data?.message ||
            err?.response?.data?.detail ||
            err?.message ||
            'Error al iniciar sesión.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div id="app" className="app app-full-height app-without-header login-v2">
      {/* Fondo a pantalla completa (con fallback por si la imagen no carga) */}
      <div className="login-cover">
        <div
          className="login-cover-img"
          style={{
            backgroundImage: `url(${BG_IMAGES[bgIndex]})`,
            backgroundColor: '#0b1220', // fallback
          }}
        />
        <div className="login-cover-bg" />
      </div>

      {/* Contenedor del formulario */}
      <div className="login-container">
        <div className="login-header d-flex align-items-center justify-between mb-4">
          <img
            src="/assets/img/logo/Devioz.png"
            alt="Devioz"
            className="devioz-logo-header"
          />
          <i className="fa fa-lock fa-2x text-white" />
        </div>

        <div className="login-content">
          <form onSubmit={handleLogin} noValidate>
            <div className="form-floating mb-3">
              <input
                type="email"
                className="form-control"
                placeholder="Email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="username"
                autoFocus
                disabled={loading}
              />
              <label>Email Address</label>
            </div>

            <div className="form-floating mb-3">
              <input
                type="password"
                className="form-control"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                disabled={loading}
              />
              <label>Password</label>
            </div>

            <div className="form-check mb-3">
              <input
                type="checkbox"
                className="form-check-input"
                id="rememberMe"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                disabled={loading}
              />
              <label className="form-check-label" htmlFor="rememberMe">
                Remember Me
              </label>
            </div>

            {error && <div className="alert alert-danger">{error}</div>}

            <button
              type="submit"
              className="btn btn-primary d-block w-100"
              disabled={loading}
            >
              {loading ? 'Ingresando…' : 'Sign me in'}
            </button>
          </form>

          <div className="text-center mt-3 text-white text-opacity-75">
            Not a member yet? Click <Link to="/register">here</Link> to register.
          </div>
        </div>
      </div>

      {/* Miniaturas para elegir fondo manualmente */}
      <div className="bg-selector">
        {BG_IMAGES.map((img, i) => (
          <button
            key={i}
            type="button"
            className={`bg-thumb ${bgIndex === i ? 'active' : ''}`}
            onClick={() => setBgIndex(i)}
            style={{ backgroundImage: `url(${img})` }}
            aria-label={`Cambiar fondo a ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
}
